import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { Link } from '@remix-run/react';
import { toast } from 'sonner';
import {
    ExternalLinkIcon,
    ForwardIcon,
    MinusIcon,
    PencilIcon,
    UserRoundIcon,
    XIcon,
} from 'lucide-react';
import { useActivitySensor } from '@archipro-website/react-hooks';
import { cn } from '@rocco/utils/cn';
import { Separator } from '@rocco/components/separator';
import type { MeUser } from '~/server/types';
import type { Conversation, TextOrFileMessage } from '../../types';
import { useChat } from '../../hooks/use-chat';
import { useConversation } from '../../hooks/use-conversation';
import { useRealTimeChat } from '../../hooks/use-real-time-chat';
import { useSendTypingIndicator } from '../../hooks/use-typing-indicator';
import {
    CHAT_BOX_CLOSED_WIDTH_REM,
    CHAT_BOX_OPEN_WIDTH_PX,
    getChatBoxMinimisedWidthRem,
    getChatParticipants,
    getChatSubtitle,
    getChatTitle,
} from '../../utils';
import { ChatBoxAvatar } from './ChatBoxAvatar';
import { ChatBoxMoreOptions } from './ChatBoxMoreOptions';
import { ChatBoxMessages } from './ChatBoxMessages';
import { ChatBoxInput } from './ChatBoxInput';
import { ChatBoxNewMessage } from './ChatBoxNewMessage';
import { ChatBoxSettings } from './settings/ChatBoxSettings';
import { NotificationBadge } from '../NotificationBadge';

/**
 * A chat box containing a single chat conversation.
 * - The chat box text size should remain the same regardless of window size,
 *   unlike the rest of the website, so it should use `px` instead of `rem`.
 */
export function ChatBox({
    className,
    user,
    conversationID,
    conversation: partialConversation,
}: {
    className?: string;
    /**
     * The current logged-in user.
     */
    user: MeUser;
    /**
     * The ID of the conversation. Set to `0` to create a new conversation.
     */
    conversationID: number;
    /**
     * The conversation object from the conversations list query, which contains
     * only the latest 7 messages. The paginated messages will be fetched when
     * this component is mounted. This prop is used to provide a preview of the
     * conversation before the messages are fully loaded. This may be `undefined`
     * if the conversation was not in the paginated conversations list query.
     */
    conversation?: Conversation;
}) {
    const chatBoxId = useId();
    const [showSettings, setShowSettings] = useState(false);
    const [isFileAttachmentSent, setIsFileAttachmentSent] = useState(false);
    const [pollingIntervalMs, setPollingIntervalMs] = useState(0);
    const [replyingTo, setReplyingTo] = useState<TextOrFileMessage | null>(
        null
    );
    const [scrollDownCounter, setScrollDownCounter] = useState(0);
    const pageActive = useActivitySensor();
    const isNewConversation = conversationID <= 0;
    const sendTypingIndicator = useSendTypingIndicator();
    const {
        pendingMessage,
        forwardedMessage,
        isChatBoxOpen,
        openChatBox,
        minimizeChatBox,
        closeChatBox,
        setPendingMessage,
        setForwardedMessage,
    } = useChat();
    const {
        conversation: fullConversation,
        messages,
        hasMoreMessages,
        isLoading,
        isSubmitting,
        isMarkAsReadBusy,
        loadMore,
        createMessage,
        markAsRead,
        deleteMessage,
    } = useConversation({
        id: conversationID,
        pollingIntervalMs,
        onDeleteError: () => {
            toast.error('Failed to delete message');
        },
    });
    const { isConnected } = useRealTimeChat({
        onMessage: (message) => {
            if (message.conversation_id === conversationID) {
                if (message.type === 'chat/removed') {
                    closeChatBox(conversationID);
                }
            }
        },
    });
    const isOpen = useMemo(() => {
        return isChatBoxOpen(conversationID);
    }, [conversationID, isChatBoxOpen]);
    const conversation = useMemo(() => {
        return fullConversation ?? partialConversation;
    }, [fullConversation, partialConversation]);
    const isParticipantMember = useMemo(() => {
        return (
            !conversation ||
            conversation.Participants.some(
                ({ Identity }) =>
                    Identity.__typename === 'Member' && Identity.ID === user.ID
            )
        );
    }, [conversation, user.ID]);
    const unreadCount = useMemo(() => {
        return conversation
            ? conversation.UnreadMessageCount || Number(conversation.Unread)
            : 0;
    }, [conversation]);
    const otherParticipants = useMemo(() => {
        return getChatParticipants(conversation, user.ID);
    }, [conversation, user.ID]);
    const title = useMemo(() => {
        if (isNewConversation) {
            return forwardedMessage ? 'Forward Message' : 'New Message';
        }
        return getChatTitle(conversation, otherParticipants);
    }, [isNewConversation, forwardedMessage, conversation, otherParticipants]);
    const subtitle = useMemo(() => {
        return getChatSubtitle(conversation, otherParticipants);
    }, [conversation, otherParticipants]);
    const isDirectConversation =
        !isNewConversation && conversation?.__typename === 'DirectConversation';

    const sendMessage = useCallback(
        (message: string, attachments: File[], replyTo?: TextOrFileMessage) => {
            if (attachments?.length) {
                setIsFileAttachmentSent(true);
            } else {
                setIsFileAttachmentSent(false);
            }
            createMessage({ message, attachments, replyTo });
            // Clear reply state after sending.
            setReplyingTo(null);
        },
        [createMessage]
    );

    // Set the polling interval based on the websocket connection status.
    useEffect(() => {
        if (isConnected || !pageActive) {
            setPollingIntervalMs(300_000); // 5 minutes
        } else {
            // Fallback to polling if the WebSocket server is not connected.
            setPollingIntervalMs(30_000); // 30 seconds
        }
    }, [isConnected, pageActive]);

    // Mark the conversation as read if the chat box is open.
    useEffect(() => {
        if (
            isParticipantMember &&
            fullConversation &&
            (fullConversation.Unread || fullConversation.UnreadMessageCount) &&
            isOpen &&
            !isMarkAsReadBusy
        ) {
            markAsRead();
        }
    }, [
        isParticipantMember,
        fullConversation,
        isOpen,
        isMarkAsReadBusy,
        markAsRead,
    ]);

    // Automatically send the pending message when this chat box was converted
    // from the new message chat box.
    useEffect(() => {
        if (pendingMessage?.conversationID === conversationID) {
            sendMessage(
                pendingMessage.message,
                pendingMessage.attachments,
                pendingMessage.replyTo
            );
            setPendingMessage(null);
        }
    }, [conversationID, pendingMessage, sendMessage, setPendingMessage]);

    return (
        <aside
            id={chatBoxId}
            className={cn(
                'flex h-[640px] flex-col rounded-[16px] transition-[height,width] duration-200 ease-linear will-change-[height,width]',
                isOpen ? 'bg-white/50 backdrop-blur-xl' : 'bg-transparent',
                className
            )}
            style={{
                width: isOpen
                    ? `${CHAT_BOX_OPEN_WIDTH_PX}px`
                    : `${getChatBoxMinimisedWidthRem(conversation)}rem`,
                height: isOpen ? undefined : `${CHAT_BOX_CLOSED_WIDTH_REM}rem`,
            }}
            data-state={isOpen ? 'open' : 'closed'}
            aria-label={title}
        >
            {/* Header */}
            <header
                className={cn(
                    'relative flex items-center gap-[12px] rounded-t-[16px]',
                    isOpen ? 'p-[12px]' : 'p-0'
                )}
                role={isOpen ? undefined : 'button'}
                aria-label={isOpen ? undefined : 'Open chat'}
                aria-expanded={isOpen}
                aria-controls={chatBoxId}
                onClick={isOpen ? undefined : () => openChatBox(conversationID)}
            >
                {/* Make sure the closed avatar/icon size matches the closed chat box size. */}
                {isNewConversation ? (
                    <div
                        className={cn(
                            'flex items-center justify-center rounded-full bg-white',
                            isOpen ? 'size-[40px]' : 'size-14 shadow-lg'
                        )}
                    >
                        {forwardedMessage ? (
                            <ForwardIcon
                                className={cn(
                                    'text-gray-600',
                                    isOpen ? 'size-[20px]' : 'size-7'
                                )}
                            />
                        ) : (
                            <PencilIcon
                                className={cn(
                                    'text-gray-600',
                                    isOpen ? 'size-[20px]' : 'size-7'
                                )}
                                strokeWidth="1.5px"
                            />
                        )}
                    </div>
                ) : (
                    <ChatBoxAvatar
                        conversation={conversation}
                        user={user}
                        isOpen={isOpen}
                    />
                )}
                <div
                    className={cn(
                        'flex min-w-0 flex-1 items-center gap-[8px]',
                        !isOpen && 'hidden'
                    )}
                >
                    <div className="min-w-0 flex-1 space-y-[2px] leading-tight text-black">
                        <div className="truncate text-[14px] font-medium">
                            {title}
                        </div>
                        {!isNewConversation &&
                            (isDirectConversation && conversation.BuildHub ? (
                                <Link
                                    to={conversation.BuildHub.Link}
                                    className="flex w-fit max-w-full items-center gap-[4px] opacity-70 hover:underline hover:opacity-100"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <span className="truncate text-[12px]">
                                        {subtitle}
                                    </span>
                                    <ExternalLinkIcon className="size-[12px] shrink-0" />
                                </Link>
                            ) : (
                                <div className="truncate text-[12px] opacity-70">
                                    {subtitle}
                                </div>
                            ))}
                    </div>
                    <NotificationBadge
                        className="h-[18px] min-w-[18px] p-[4px] text-[11px]"
                        count={unreadCount}
                    />
                    {isDirectConversation && (
                        <button
                            className="flex items-center gap-[6px] rounded-[8px] border border-black/50 px-[8px] py-[4px] text-[13px] text-black/70 hover:bg-black/10"
                            aria-label="Chat settings"
                            onClick={() => setShowSettings((state) => !state)}
                        >
                            <UserRoundIcon className="size-[16px]" />
                            {conversation.Participants.length}
                        </button>
                    )}
                    <div className="flex items-center gap-[2px]">
                        {!!conversation && (
                            <ChatBoxMoreOptions conversation={conversation} />
                        )}
                        <button
                            className="flex size-[36px] items-center justify-center rounded-full hover:bg-black/10"
                            aria-label="Minimize chat box"
                            onClick={() => minimizeChatBox(conversationID)}
                        >
                            <MinusIcon className="size-[16px]" />
                        </button>
                        <button
                            className="flex size-[36px] items-center justify-center rounded-full hover:bg-black/10"
                            aria-label="Close chat box"
                            onClick={() => closeChatBox(conversationID)}
                        >
                            <XIcon className="size-[16px]" />
                        </button>
                    </div>
                </div>
                {!isOpen && (
                    <NotificationBadge
                        className="absolute -right-1 -top-1"
                        count={unreadCount}
                    />
                )}
            </header>
            <section
                className={cn(
                    'flex min-h-0 flex-1 flex-col gap-[8px] p-[8px] pb-8',
                    !isOpen && 'hidden'
                )}
            >
                {isNewConversation ? (
                    <ChatBoxNewMessage
                        className="min-h-0 flex-1"
                        isOpen={isOpen}
                    />
                ) : showSettings && isDirectConversation ? (
                    <ChatBoxSettings
                        className="min-h-0 flex-1"
                        conversation={conversation}
                        onClose={() => setShowSettings(false)}
                    />
                ) : (
                    <>
                        <Separator className="bg-white" />
                        <ChatBoxMessages
                            className="min-h-0 flex-1"
                            conversation={conversation}
                            messages={
                                fullConversation
                                    ? messages
                                    : (conversation?.Messages.nodes ?? [])
                            }
                            scrollDownCounter={scrollDownCounter}
                            isOpen={isOpen}
                            isLoadingMore={isLoading}
                            hasMore={hasMoreMessages}
                            onLoadMore={loadMore}
                            onDeleteMessage={deleteMessage}
                            onReplyMessage={(message) => {
                                setReplyingTo(message);
                                setScrollDownCounter((state) => state + 1);
                            }}
                            onForwardMessage={(message) => {
                                setForwardedMessage(message);
                                openChatBox(0);
                            }}
                        />
                        <ChatBoxInput
                            conversationID={conversationID}
                            isOpen={isOpen}
                            isDisabled={!fullConversation}
                            isNotParticipant={!isParticipantMember}
                            // Only show the submitting state when a message is sent
                            // WITH file attachments. This is prevent sending too quickly
                            // causing messages being sent in the wrong order.
                            isSubmitting={isSubmitting && isFileAttachmentSent}
                            replyingTo={replyingTo}
                            replyMode="reply"
                            onCancelReply={() => {
                                setReplyingTo(null);
                                setScrollDownCounter((state) => state + 1);
                            }}
                            onSend={({ message, attachments, replyTo }) => {
                                sendMessage(message, attachments, replyTo);
                            }}
                            onAttachmentsChanged={() => {
                                setScrollDownCounter((state) => state + 1);
                            }}
                            onTyping={() => {
                                sendTypingIndicator(conversationID);
                            }}
                        />
                    </>
                )}
            </section>
        </aside>
    );
}
